import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const NotFound: React.FC = () => {
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([]);
  
  useEffect(() => {
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 5,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-950 via-amber-900 to-orange-800 relative overflow-hidden">
      {/* Animated rust particles */}
      <div className="absolute inset-0 pointer-events-none">
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-1 h-1 bg-orange-400/30 rounded-full"
            initial={{ 
              x: `${particle.x}%`, 
              y: -10,
              opacity: 0 
            }}
            animate={{
              y: '110vh',
              opacity: [0, 0.8, 0],
            }}
            transition={{
              duration: 8 + particle.delay,
              repeat: Infinity,
              delay: particle.delay,
              ease: 'linear',
            }}
            style={{ left: `${particle.x}%` }}
          />
        ))}
      </div>

      {/* Garden machinery silhouettes */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-radial from-orange-600 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-radial from-amber-600 to-transparent rounded-full blur-3xl" />
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4">
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            {/* 404 Text with rust effect */}
            <motion.h1 
              className="text-8xl md:text-9xl font-bold mb-4 relative inline-block"
              animate={{ 
                textShadow: [
                  '0 0 20px rgba(251, 146, 60, 0.5)',
                  '0 0 40px rgba(251, 146, 60, 0.8)',
                  '0 0 20px rgba(251, 146, 60, 0.5)',
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <span className="bg-gradient-to-b from-orange-300 via-orange-500 to-orange-700 bg-clip-text text-transparent">
                404
              </span>
              <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 to-transparent" />
            </motion.h1>

            {/* Garden maintenance message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mb-8"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-orange-200 mb-4">
                The Rust Garden is Under Maintenance
              </h2>
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-orange-400 to-transparent mx-auto mb-6" />
              <p className="text-lg md:text-xl text-orange-300/80 mb-4">
                Our ancient machinery needs tending to...
              </p>
              <p className="text-md text-orange-400/60 max-w-md mx-auto">
                The oxidized gears are being oiled, the corroded pipes are being cleaned, 
                and the rust sprites are reorganizing this section of the realm.
              </p>
            </motion.div>

            {/* Navigation options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link to="/">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-orange-500/25 transition-all duration-300"
                >
                  Return to the Main Path
                </motion.button>
              </Link>
              
              <Link to="/realms/rust-garden">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 border-2 border-orange-500 text-orange-300 font-semibold rounded-lg hover:bg-orange-500/10 transition-all duration-300"
                >
                  Visit Rust Garden Realm
                </motion.button>
              </Link>
            </motion.div>

            {/* Decorative bottom element */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 0.9, duration: 1 }}
              className="mt-16"
            >
              <div className="flex justify-center space-x-2">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 bg-orange-400 rounded-full"
                    animate={{
                      y: [0, -10, 0],
                      opacity: [0.3, 1, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.2,
                    }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Maintenance status */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-8 text-orange-500/60 text-sm"
            >
              <p>Error Code: RUST_OVERFLOW_404</p>
              <p className="mt-1">The page you seek has oxidized away...</p>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Animated gear decoration */}
      <motion.div
        className="absolute bottom-0 right-0 w-48 h-48 opacity-20"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full fill-orange-600">
          <path d="M50 10 L55 25 L70 20 L70 35 L85 35 L80 50 L85 65 L70 65 L70 80 L55 75 L50 90 L45 75 L30 80 L30 65 L15 65 L20 50 L15 35 L30 35 L30 20 L45 25 Z" />
          <circle cx="50" cy="50" r="20" className="fill-orange-900" />
        </svg>
      </motion.div>

      {/* Animated gear decoration left */}
      <motion.div
        className="absolute top-20 left-0 w-32 h-32 opacity-10"
        animate={{ rotate: -360 }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full fill-orange-700">
          <path d="M50 10 L55 25 L70 20 L70 35 L85 35 L80 50 L85 65 L70 65 L70 80 L55 75 L50 90 L45 75 L30 80 L30 65 L15 65 L20 50 L15 35 L30 35 L30 20 L45 25 Z" />
          <circle cx="50" cy="50" r="20" className="fill-orange-950" />
        </svg>
      </motion.div>
    </div>
  );
};

export default NotFound;